// SchoolProfilePanel — lets a logged-in school (School or Admin mode) view/edit
// their own school's name, location and contact email. No password/code here —
// those stay Super Admin only.

import { useEffect, useState } from 'react';
import { getMySchool, updateMySchool } from '../api/client';
import MapPickerModal from './MapPickerModal';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import { resizeImage } from '../ui/format';

const BOARDS = [
  { value: 'CBSE',  label: 'CBSE' },
  { value: 'ICSE',  label: 'ICSE' },
  { value: 'RBSE',  label: 'Rajasthan Board (RBSE)' },
  { value: 'State', label: 'Other State Board' },
  { value: 'Other', label: 'Other' },
];

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function SchoolProfilePanel() {
  const [form, setForm] = useState({ name: '', code: '', location: '', contact_email: '', phone: '', latitude: '', longitude: '', weekly_off_days: [0], board: 'CBSE', reco_no: '', reg_no: '', dise_code: '', logo_data_url: '' });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [currentSession, setCurrentSession] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const s = await getMySchool();
      setForm({
        name: s.name, code: s.code, location: s.location || '',
        contact_email: s.contact_email || '', phone: s.phone || '',
        latitude: s.latitude ?? '', longitude: s.longitude ?? '',
        weekly_off_days: s.weekly_off_days && s.weekly_off_days.length ? s.weekly_off_days : [0],
        board: s.board || 'CBSE',
        reco_no: s.reco_no || '', reg_no: s.reg_no || '', dise_code: s.dise_code || '',
        logo_data_url: s.logo_data_url || '',
      });
      setCurrentSession(s.current_session || '');
    } catch (err) {
      setMsg('Failed to load: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }

  async function onLogoChosen(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true); setMsg('');
    try {
      const dataUrl = await resizeImage(file, 300);
      setForm(f => ({ ...f, logo_data_url: dataUrl }));
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setLogoBusy(false);
    }
  }

  function toggleOffDay(value) {
    setForm(f => ({
      ...f,
      weekly_off_days: f.weekly_off_days.includes(value)
        ? f.weekly_off_days.filter(d => d !== value)
        : [...f.weekly_off_days, value].sort((a, b) => a - b),
    }));
  }

  async function save(e) {
    e.preventDefault();
    setMsg('');
    try {
      await updateMySchool({
        name: form.name, location: form.location, contact_email: form.contact_email, phone: form.phone,
        latitude: form.latitude === '' ? null : parseFloat(form.latitude),
        longitude: form.longitude === '' ? null : parseFloat(form.longitude),
        weekly_off_days: form.weekly_off_days,
        board: form.board,
        reco_no: form.reco_no, reg_no: form.reg_no, dise_code: form.dise_code, logo_data_url: form.logo_data_url,
      });
      setMsg('School profile updated ✅');
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  if (loading) return <p className="text-ink-soft">Loading…</p>;

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";
  const helpCls = "font-normal text-xs text-ink-soft normal-case tracking-normal mt-1.5 block";

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-xl text-ink mb-1">School Profile</h2>
        <p className="text-sm text-ink-soft">Update your school's name, location and contact email. To change your school code or password, contact your platform administrator.</p>
      </div>

      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <Card className="p-5 max-w-[460px]">
        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>School Code (fixed)
            <input disabled className={inputCls + ' bg-line-soft text-ink-soft'} value={form.code} />
          </label>

          <label className={labelCls}>School Name
            <input required className={inputCls} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
          </label>

          <label className={labelCls}>Location
            <input required className={inputCls} placeholder="e.g. Jaunpur, Uttar Pradesh"
              value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </label>

          <label className={labelCls}>Contact Email
            <input type="email" className={inputCls} placeholder="e.g. admin@school.edu"
              value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
          </label>

          <label className={labelCls}>Contact Phone
            <input type="tel" className={inputCls} placeholder="e.g. +91 98765 43210"
              value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <span className={helpCls}>Shown to drivers as a fallback number if an SOS alert fails to send</span>
          </label>

          <label className={labelCls}>Examining Board
            <select className={inputCls} value={form.board} onChange={e => setForm({ ...form, board: e.target.value })}>
              {BOARDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            <span className={helpCls}>
              Sets the report card's grading scale — CBSE prints letter grades (A1–E); ICSE, Rajasthan Board and other
              state boards print a pass division (Distinction / First / Second / Third) instead. Double-check the exact
              cutoffs against your board's current circular before using a printed report card as an official certificate.
            </span>
          </label>

          <div className="border-t border-line pt-3 mt-1">
            <p className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-2.5">Report Card Letterhead</p>

            <label className={labelCls}>School Crest / Logo
              <div className="flex items-center gap-3 mt-1">
                <div className="w-14 h-14 rounded-full border border-line flex items-center justify-center overflow-hidden bg-paper-raised shrink-0">
                  {form.logo_data_url
                    ? <img src={form.logo_data_url} alt="" className="w-full h-full object-cover" />
                    : <span className="text-[9px] text-ink-soft text-center leading-tight">No logo</span>}
                </div>
                <label className="cursor-pointer">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-s border border-line text-xs font-bold text-ink hover:bg-paper-raised">
                    {logoBusy ? 'Uploading…' : 'Upload image'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" disabled={logoBusy} onChange={onLogoChosen} />
                </label>
                {form.logo_data_url && (
                  <button type="button" className="text-xs font-bold text-critical" onClick={() => setForm(f => ({ ...f, logo_data_url: '' }))}>
                    Remove
                  </button>
                )}
              </div>
              <span className={helpCls}>Printed top-left on the report card header.</span>
            </label>

            <label className={labelCls + ' mt-3'}>Reco. No. <span className="font-normal normal-case text-ink-soft">(optional)</span>
              <input className={inputCls} placeholder="e.g. 465-470/10"
                value={form.reco_no} onChange={e => setForm({ ...form, reco_no: e.target.value })} />
            </label>

            <label className={labelCls + ' mt-3'}>Reg. No.
              <input className={inputCls} placeholder="e.g. 150/JPR/1987-88"
                value={form.reg_no} onChange={e => setForm({ ...form, reg_no: e.target.value })} />
            </label>

            <label className={labelCls + ' mt-3'}>DISE / UDISE+ Code
              <input className={inputCls} placeholder="e.g. 08050702225"
                value={form.dise_code} onChange={e => setForm({ ...form, dise_code: e.target.value })} />
            </label>

            <label className={labelCls + ' mt-3'}>Session
              <input disabled className={inputCls + ' bg-line-soft text-ink-soft'}
                value={currentSession || 'No current academic session set'} />
              <span className={helpCls}>Comes from your current Academic Session, not edited here.</span>
            </label>
          </div>

          <label className={labelCls}>Weekly Off Days
            <div className="flex flex-wrap gap-3 mt-1">
              {WEEKDAYS.map(w => (
                <label key={w.value} className="flex items-center gap-1.5 font-normal text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={form.weekly_off_days.includes(w.value)}
                    onChange={() => toggleOffDay(w.value)}
                    className="accent-transit"
                  />
                  {w.label}
                </label>
              ))}
            </div>
            <span className={helpCls}>
              Days the school is closed every week (e.g. just Sunday, or Sunday + Saturday). Used to compute
              correct attendance % in reports — these days are never counted against staff or students.
            </span>
          </label>

          <label className={labelCls}>School Location (for staff check-in geofencing)
            <div className="flex gap-2 items-center mt-1">
              <span className="font-normal text-sm text-ink">
                {form.latitude !== '' && form.longitude !== ''
                  ? `${Number(form.latitude).toFixed(6)}, ${Number(form.longitude).toFixed(6)}`
                  : 'Not set — staff check-in distance can\'t be checked yet'}
              </span>
              <Button variant="secondary" type="button" className="!px-3 !py-1.5 !text-xs whitespace-nowrap" onClick={() => setShowMapPicker(true)}>
                Set on Map
              </Button>
            </div>
            <span className={helpCls}>Staff self-check-in is flagged if it happens more than 1km from this point — set it accurately.</span>
          </label>

          <Button type="submit" className="self-start mt-1">Save Changes</Button>
        </form>
      </Card>

      {showMapPicker && (
        <MapPickerModal
          initialLat={form.latitude  !== '' ? parseFloat(form.latitude)  : undefined}
          initialLng={form.longitude !== '' ? parseFloat(form.longitude) : undefined}
          onConfirm={(lat, lng) => {
            setForm({ ...form, latitude: String(lat), longitude: String(lng) });
            setShowMapPicker(false);
          }}
          onClose={() => setShowMapPicker(false)}
        />
      )}
    </div>
  );
}
